import { useState, useMemo, useCallback, useEffect } from "react";
import BIDNavbar from "@/components/feature/BIDNavbar";
import BIDFooter from "@/components/feature/BIDFooter";
import { useAuth } from "@/hooks/useAuth";
import StatsOverview from "./components/StatsOverview";
import TicketList from "./components/TicketList";
import TicketDetailModal from "./components/TicketDetailModal";
import type { ZammadUserTicket } from "@/mocks/zammadTickets";
import { fetchLiveMyTickets } from "@/services/zammadService";

type StatusFilter = "all" | "open" | "in_progress" | "resolved" | "closed";

const filterTabs: { key: StatusFilter; label: string; icon: string }[] = [
  { key: "all", label: "Todos", icon: "ri-file-list-3-line" },
  { key: "open", label: "Abiertos", icon: "ri-inbox-line" },
  { key: "in_progress", label: "En Progreso", icon: "ri-loader-4-line" },
  { key: "resolved", label: "Resueltos", icon: "ri-check-line" },
  { key: "closed", label: "Cerrados", icon: "ri-check-double-line" },
];

export default function MisTicketsPage() {
  const { user, loading, login, loginWithCredentials, logout, error } = useAuth();
  const [activeFilter, setActiveFilter] = useState<StatusFilter>("all");
  const [selectedTicket, setSelectedTicket] = useState<ZammadUserTicket | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tickets, setTickets] = useState<ZammadUserTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);

  // States para login local de Zammad
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);

  const handleLogin = useCallback(() => {
    sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search);
    login();
  }, [login]);

  const handleCredentialsLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) return;

    setLoadingCredentials(true);
    setCredentialsError(null);
    try {
      await loginWithCredentials(username, password);
    } catch (err) {
      setCredentialsError(err instanceof Error ? err.message : "Error al iniciar sesión");
    } finally {
      setLoadingCredentials(false);
    }
  }, [username, password, loginWithCredentials]);

  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchTicketsSilently = useCallback(async (isInitial = false) => {
    if (!user) return;
    if (isInitial) setLoadingTickets(true);
    setIsSyncing(true);
    setTicketsError(null);
    try {
      const data = await fetchLiveMyTickets({
        userId: user.id,
        email: user.email,
        forceRefresh: !isInitial,
      });
      setTickets(data || []);
      const refreshedAt = new Date();
      setLastRefreshedAt(refreshedAt);
      sessionStorage.setItem(
        `zammad_my_tickets_v1_${user.id}`,
        JSON.stringify({ data: data || [], fetchedAt: refreshedAt.toISOString() })
      );
    } catch (err) {
      console.error("Error loading tickets:", err);
      setTicketsError("No fue posible actualizar tus tickets. Se conserva la última información disponible.");
    } finally {
      if (isInitial) setLoadingTickets(false);
      setIsSyncing(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setTickets([]);
      return;
    }

    const cached = sessionStorage.getItem(`zammad_my_tickets_v1_${user.id}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed?.data)) {
          setTickets(parsed.data);
          if (parsed.fetchedAt) setLastRefreshedAt(new Date(parsed.fetchedAt));
        }
      } catch {
        // Ignore malformed cache; the live request below remains authoritative.
      }
    }

    void fetchTicketsSilently(true);
  }, [user, fetchTicketsSilently]);

  const myTickets = tickets;

  const filteredTickets = useMemo(() => {
    let result = myTickets;

    if (activeFilter !== "all") {
      result = result.filter((t) => t.status === activeFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q)
      );
    }

    return [...result].sort((a, b) => {
      const dateDifference = Date.parse(b.createdAt) - Date.parse(a.createdAt);
      return Number.isFinite(dateDifference) && dateDifference !== 0
        ? dateDifference
        : b.zammadId - a.zammadId;
    });
  }, [myTickets, activeFilter, searchQuery]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Reset pagination when active filter or search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery]);

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTickets.slice(start, start + itemsPerPage);
  }, [filteredTickets, currentPage, itemsPerPage]);

  const handleTicketClick = useCallback((ticket: ZammadUserTicket) => {
    setSelectedTicket(ticket);
  }, []);

  const handleTicketUpdated = useCallback((updatedTicket: ZammadUserTicket) => {
    setTickets((prev) =>
      prev.map((ticket) =>
        ticket.zammadId === updatedTicket.zammadId ? updatedTicket : ticket
      )
    );
    setSelectedTicket(updatedTicket);
  }, []);

  if (loading) {
    return (
      <main className="relative min-h-screen bg-background-50 flex flex-col justify-between">
        <BIDNavbar />
        <div className="flex-1 flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500 font-medium">Cargando perfil corporativo...</p>
        </div>
        <BIDFooter />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="relative min-h-screen bg-background-50 flex flex-col justify-between">
        <BIDNavbar />
        
        {/* Pantalla de Inicio de Sesión */}
        <section className="flex-1 flex items-center justify-center pt-28 pb-16 px-4 md:px-6">
          <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 shadow-xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
              <i className="ri-shield-user-line text-primary-500 text-3xl"></i>
            </div>
            
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-1">
              Seguimiento de Tickets
            </h1>
            
            <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
              Ingresa tus credenciales o inicia sesión con tu cuenta corporativa.
            </p>
            
            {(credentialsError || error) && (
              <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-600 text-left flex items-start gap-2.5">
                <i className="ri-error-warning-line text-sm mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="font-semibold">Error de autenticación</p>
                  <p className="mt-0.5 text-red-500">{credentialsError || error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleCredentialsLogin} className="space-y-4 text-left">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                  Usuario o Correo
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                    placeholder="ejemplo@bidgroup.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5 uppercase tracking-wider">
                  Contraseña
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-background-200 bg-background-50/50 text-sm focus:outline-none focus:border-primary-500 focus:bg-white transition-all"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loadingCredentials}
                className="w-full py-3.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {loadingCredentials ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <i className="ri-login-box-line text-base"></i>
                    Ingresar
                  </>
                )}
              </button>
            </form>

            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200"></div>
              </div>
              <span className="relative px-3 bg-white text-xs text-foreground-400 uppercase tracking-wider font-medium">
                o continuar con
              </span>
            </div>
            
            <button
              onClick={handleLogin}
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl border border-background-200 bg-white text-foreground-800 text-sm font-semibold hover:bg-background-50 transition-all cursor-pointer shadow-sm hover:shadow active:scale-[0.98]"
            >
              <i className="ri-microsoft-fill text-lg text-primary-500"></i>
              Iniciar sesión con Microsoft
            </button>
            
            <p className="text-[11px] text-foreground-400 mt-6 leading-relaxed">
              Mesa de Ayuda BID Group protege tus datos mediante encriptación y conexiones seguras.
            </p>
          </div>
        </section>
        
        <BIDFooter />
      </main>
    );
  }

  return (
    <main className="relative min-h-screen bg-background-50">
      <BIDNavbar />

      {/* Page Header */}
      <section className="w-full pt-24 md:pt-28 pb-8 md:pb-10 px-4 md:px-6 lg:px-10 bg-background-100/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <a
                  href="/"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-50 border border-background-200 text-xs font-semibold text-foreground-700 hover:text-primary-600 hover:border-primary-200 transition-all shadow-sm cursor-pointer"
                >
                  <i className="ri-home-4-line text-sm"></i>
                  <span>Regresar al Inicio</span>
                </a>
                <span className="text-foreground-300">/</span>
                <span className="text-sm text-foreground-700 font-medium">Mis tickets</span>

                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ml-2 ${
                  ticketsError
                    ? "bg-amber-50 border-amber-200/80 text-amber-700"
                    : "bg-emerald-50 border-emerald-200/80 text-emerald-700"
                }`}>
                  <span className="relative flex h-2 w-2">
                    {!ticketsError && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${ticketsError ? "bg-amber-500" : "bg-emerald-500"}`}></span>
                  </span>
                  {ticketsError ? "Sin conexión" : "Información actualizada"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  Mis Tickets
                </h1>
                <button
                  onClick={() => fetchTicketsSilently(false)}
                  disabled={isSyncing}
                  className="p-2 rounded-xl bg-background-50 border border-background-200 text-foreground-600 hover:text-primary-600 hover:border-primary-200 transition-all cursor-pointer shadow-sm active:scale-95 disabled:opacity-50"
                  title="Actualizar datos"
                >
                  <i className={`ri-refresh-line text-base ${isSyncing ? "animate-spin text-primary-500" : ""}`}></i>
                </button>
              </div>
              <p className="text-sm text-foreground-600 max-w-xl mt-1">
                Consulta el estatus de tus tickets y usa el botón de sincronización cuando necesites actualizar la información.
              </p>
            </div>

            {/* User Card + Logout Button */}
            {user && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary-700">{user.avatarInitials}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{user.name}</p>
                    <p className="text-xs text-foreground-500 truncate">{user.department} &bull; {user.email}</p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Sesión activa"></div>
                </div>

                <button
                  onClick={logout}
                  className="flex items-center justify-center w-11 h-11 rounded-xl bg-background-50 border border-background-200/70 text-foreground-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all cursor-pointer"
                  title="Cerrar sesión"
                >
                  <i className="ri-logout-box-r-line text-lg"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats - Personales */}
      <section className="w-full py-6 md:py-8 px-4 md:px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          {ticketsError && myTickets.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-3">
              <i className="ri-error-warning-line text-lg mt-0.5"></i>
              <div>
                <p className="font-semibold">No se pudieron consultar tus tickets</p>
                <p className="text-xs mt-1">Tu sesión o la conexión con Zammad debe revisarse. Intenta nuevamente con el botón de actualizar.</p>
              </div>
            </div>
          ) : (
            <>
              {ticketsError && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  {ticketsError} Última actualización: {lastRefreshedAt.toLocaleString()}.
                </div>
              )}
              <StatsOverview tickets={myTickets} />
            </>
          )}
        </div>
      </section>

      {/* Filter + Search + Tickets */}
      <section className="w-full py-6 md:py-8 px-4 md:px-6 lg:px-10 bg-background-100/60">
        <div className="max-w-6xl mx-auto">
          {/* Filter Tabs + Search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200/70 overflow-x-auto">
              {filterTabs.map((tab) => {
                const count = tab.key === "all"
                  ? myTickets.length
                  : myTickets.filter((t) => t.status === tab.key).length;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveFilter(tab.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all ${
                      activeFilter === tab.key
                        ? "bg-accent-500 text-white"
                        : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                    }`}
                  >
                    <i className={`${tab.icon} text-[11px]`}></i>
                    {tab.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                      activeFilter === tab.key
                        ? "bg-white/20 text-white"
                        : "bg-background-200 text-foreground-500"
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative flex-shrink-0 w-full sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar en mis tickets..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  <i className="ri-close-circle-line text-foreground-400 hover:text-foreground-600 text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {/* Results info */}
          {searchQuery.trim() && (
            <p className="text-xs text-foreground-500 mb-4">
              {filteredTickets.length} resultado{filteredTickets.length !== 1 ? "s" : ""} para "{searchQuery}"
            </p>
          )}

          {/* Ticket List */}
          {loadingTickets ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-10 h-10 border-3 border-accent-500/20 border-t-accent-500 rounded-full animate-spin mb-3"></div>
              <p className="text-xs text-foreground-500">Cargando datos...</p>
            </div>
          ) : (
            <>
              <TicketList tickets={paginatedTickets} onTicketClick={handleTicketClick} />

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-background-200/80">
                  <p className="text-xs text-foreground-500">
                    Mostrando <span className="font-semibold text-foreground-800">{(currentPage - 1) * itemsPerPage + 1}</span> a{" "}
                    <span className="font-semibold text-foreground-800">
                      {Math.min(currentPage * itemsPerPage, filteredTickets.length)}
                    </span>{" "}
                    de <span className="font-semibold text-foreground-800">{filteredTickets.length}</span> tickets
                  </p>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded-lg border border-background-200 bg-white text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-sm"></i>
                      Anterior
                    </button>

                    <div className="flex items-center gap-1 px-1">
                      {(() => {
                        const getPageNumbers = () => {
                          if (totalPages <= 6) {
                            return Array.from({ length: totalPages }, (_, i) => i + 1);
                          }

                          // If > 6 pages, format as: [1, ..., current-1, current, current+1, ..., totalPages]
                          const pages: (number | string)[] = [];
                          
                          if (currentPage <= 3) {
                            pages.push(1, 2, 3, 4, "...", totalPages);
                          } else if (currentPage >= totalPages - 2) {
                            pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                          } else {
                            pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
                          }

                          return pages;
                        };

                        return getPageNumbers().map((page, idx) => {
                          if (typeof page === "string") {
                            return (
                              <span key={`ellipsis-${idx}`} className="w-7 h-7 flex items-center justify-center text-xs text-foreground-400 font-medium select-none">
                                {page}
                              </span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-7 h-7 rounded-lg text-xs font-semibold flex items-center justify-center transition-all cursor-pointer ${
                                currentPage === page
                                  ? "bg-accent-500 text-white shadow-sm"
                                  : "bg-white border border-background-200 text-foreground-600 hover:bg-background-100"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 rounded-lg border border-background-200 bg-white text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1 cursor-pointer"
                    >
                      Siguiente
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA - New Ticket */}
      <section className="w-full py-10 px-4 md:px-6 lg:px-10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-lg font-heading font-bold text-foreground-950 mb-1">
            ¿Necesitas reportar algo nuevo?
          </h2>
          <p className="text-xs text-foreground-500 mb-5">
            Crea un nuevo ticket desde el portal de Mesa de Servicios TI y podrás darle seguimiento aquí.
          </p>
          <a
            href="/ti"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
          >
            <i className="ri-add-circle-line text-sm"></i>
            Ir a Mesa de Servicios TI
          </a>
        </div>
      </section>

      <BIDFooter />

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          customerIdentity={user?.email || user?.id}
          isOpen
          onClose={() => setSelectedTicket(null)}
          onTicketUpdated={handleTicketUpdated}
        />
      )}
    </main>
  );
}
